// The AI pencil in the message box, and the card it opens above the composer.
// Tapping it asks the API for a short summary of the last five back-and-forths
// in the group (POST /groups/{row}/chat-summary). Nothing is sent to the group;
// the card is for the advisor only and closes with the X.

import { Loader2, PenLine, Sparkle, X } from "lucide-react";
import { useSummariseGroupChat } from "../../lib/api";

export function useAiSummary(row) {
  const summarise = useSummariseGroupChat();
  return {
    open: summarise.isPending || summarise.isSuccess || summarise.isError,
    loading: summarise.isPending,
    data: summarise.data,
    error: summarise.error,
    request: () => row && !summarise.isPending && summarise.mutate(row),
    close: () => summarise.reset(),
  };
}

export function AiPencilButton({ onClick, disabled, busy }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      data-testid="desk-ai-summary"
      aria-label="Summarise the last 5 exchanges"
      title="Summarise the last 5 exchanges"
      className="relative -ml-[6px] flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full border-0 bg-transparent text-msd-royal hover:bg-msd-royal-soft disabled:cursor-not-allowed disabled:text-msd-faint"
    >
      {busy ? (
        <Loader2 size={16} className="animate-spin" />
      ) : (
        <>
          <PenLine size={16} strokeWidth={2.2} />
          <Sparkle size={9} strokeWidth={2.6} className="absolute right-[4px] top-[4px] fill-current" />
        </>
      )}
    </button>
  );
}

function errorText(error) {
  const detail = error?.response?.data?.detail;
  return typeof detail === "string" ? detail : "Could not make the summary. Please try again.";
}

export function AiSummaryCard({ summary }) {
  if (!summary.open) return null;
  const text = String(summary.data?.summary || "").trim();
  const bullets = text
    .split("\n")
    .map((l) => l.replace(/^\s*[-•*]\s*/, "").trim())
    .filter(Boolean);
  return (
    <div
      className="rounded-xl bg-msd-surface px-3 py-[10px] shadow-msd-e1"
      data-testid="desk-ai-summary-card"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-2">
        <Sparkle size={13} className="shrink-0 fill-current text-msd-royal" />
        <span className="grow text-[13px] font-semibold text-msd-ink">Summary of the last 5 exchanges</span>
        <button
          type="button"
          onClick={summary.close}
          aria-label="Close summary"
          className="flex h-6 w-6 items-center justify-center rounded-full border-0 bg-transparent text-msd-muted hover:bg-msd-ground"
        >
          <X size={14} />
        </button>
      </div>
      <div className="mt-[6px] text-[14px] leading-[1.45] text-msd-ink2">
        {summary.loading && <p className="m-0 text-msd-muted">Reading the chat…</p>}
        {summary.error && <p className="m-0 text-[#B42318]">{errorText(summary.error)}</p>}
        {!summary.loading && !summary.error && bullets.length === 0 && (
          <p className="m-0 text-msd-muted">There are no messages to summarise yet.</p>
        )}
        {bullets.length > 0 && (
          <ul className="m-0 list-disc space-y-[2px] pl-5">
            {bullets.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
